import React, { useEffect, useState } from "react";
import { CameraIcon, TrashIcon } from "@heroicons/react/24/outline";
import Swal from "sweetalert2";
import { ImageWithFallback } from "../../../../Utils/loader";
import { UrduInput } from "../../../../Components/Shared/UrduInput";
import { UiModal } from "../../../../Components/Shared/UiModal";
import { useProjects } from "../../useHook";
import { getFinishingParentCategoryId, getParentCategoryLabel } from "../parentCategoryLabels";

const REQUIRED_WIDTH = 450;
const REQUIRED_HEIGHT = 450;
const MAX_NAME = 25;
const MAX_DESCRIPTION = 70;

const EMPTY_FORM = {
  name: "",
  subCategoryName_ur: "",
  description: "",
  description_ur: "",
  parentCategoryId: "",
  customUrl: "",
};

const toMediaUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http") || path.startsWith("blob:")) return path;
  const baseUrl = (process.env.REACT_APP_BASE_URL || "").replace(/\/$/, "");
  return `${baseUrl}${path.startsWith("/") ? "" : "/"}${path}`;
};

function OurProjectCategoryModal({
  isOpen,
  onClose,
  currentOurProjectCategory,
  onSuccess,
  parentCategories = [],
}) {
  const { addSubCategory, updateSubCategory } = useProjects();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const revokeBlobPreview = (preview) => {
    if (preview && preview.startsWith("blob:")) {
      URL.revokeObjectURL(preview);
    }
  };

  useEffect(() => {
    if (!isOpen) {
      setFormData(EMPTY_FORM);
      setImageFile(null);
      setImagePreview((prev) => {
        revokeBlobPreview(prev);
        return null;
      });
      setErrors({});
      setLoading(false);
      return;
    }

    if (currentOurProjectCategory) {
      setFormData({
        name: currentOurProjectCategory.name || "",
        subCategoryName_ur: currentOurProjectCategory.subCategoryName_ur || "",
        description: currentOurProjectCategory.description || "",
        description_ur: currentOurProjectCategory.description_ur || "",
        parentCategoryId: String(
          currentOurProjectCategory.parent_id ||
            currentOurProjectCategory.parentCategoryId ||
            "",
        ),
        customUrl: (currentOurProjectCategory.customUrl || "").replace(
          /\/$/,
          "",
        ),
      });
      setImagePreview(toMediaUrl(currentOurProjectCategory.image_url));
    } else {
      setFormData({
        ...EMPTY_FORM,
        parentCategoryId: getFinishingParentCategoryId(parentCategories),
      });
      setImagePreview(null);
    }
    setImageFile(null);
    setErrors({});
  }, [isOpen, currentOurProjectCategory]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    const maxLengths = {
      name: MAX_NAME,
      description: MAX_DESCRIPTION,
    };
    if (maxLengths[name] && value.length > maxLengths[name]) {
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleImageChange = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;

    if (!file.type.startsWith("image/")) {
      setErrors((prev) => ({
        ...prev,
        image: "Please select a valid image file.",
      }));
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      setErrors((prev) => ({
        ...prev,
        image: "Image size should be less than 5MB.",
      }));
      return;
    }

    const objectUrl = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      if (img.width !== REQUIRED_WIDTH || img.height !== REQUIRED_HEIGHT) {
        setErrors((prev) => ({
          ...prev,
          image: `Image resolution must be exactly ${REQUIRED_WIDTH}x${REQUIRED_HEIGHT} pixels.`,
        }));
        return;
      }

      setImagePreview((prev) => {
        revokeBlobPreview(prev);
        return URL.createObjectURL(file);
      });
      setImageFile(file);
      setErrors((prev) => ({ ...prev, image: "" }));
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      setErrors((prev) => ({
        ...prev,
        image: "Failed to load image. Please try again.",
      }));
    };
    img.src = objectUrl;
  };

  const removeImage = () => {
    setImagePreview((prev) => {
      revokeBlobPreview(prev);
      return null;
    });
    setImageFile(null);
  };

  const validateForm = () => {
    const nextErrors = {};
    if (!formData.name.trim()) {
      nextErrors.name = "Sub category name is required.";
    }
    if (!formData.parentCategoryId) {
      nextErrors.parentCategoryId = "Parent category is required.";
    }
    if (formData.customUrl.trim().startsWith("/")) {
      nextErrors.customUrl = "Custom URL should not start with '/'";
    }
    if (!imageFile && !imagePreview) {
      nextErrors.image = "Category image is required.";
    }
    setErrors(nextErrors);
    return Object.keys(nextErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setLoading(true);
    try {
      const formDataObj = new FormData();
      formDataObj.append("name", formData.name.trim());
      if (formData.subCategoryName_ur) {
        formDataObj.append("subCategoryName_ur", formData.subCategoryName_ur);
      }
      formDataObj.append("description", formData.description);
      if (formData.description_ur) {
        formDataObj.append("description_ur", formData.description_ur);
      }
      formDataObj.append("parent_id", formData.parentCategoryId);
      if (formData.customUrl.trim()) {
        formDataObj.append("customUrl", formData.customUrl.trim());
      }
      if (imageFile) {
        formDataObj.append("image", imageFile);
      }

      if (currentOurProjectCategory) {
        await updateSubCategory(currentOurProjectCategory.id, formDataObj);
        Swal.fire({
          title: "Success",
          text: "Project sub category updated successfully",
          icon: "success",
        });
      } else {
        await addSubCategory(formDataObj);
        Swal.fire({
          title: "Success",
          text: "Project sub category added successfully",
          icon: "success",
        });
      }

      if (onSuccess) {
        await onSuccess();
      }
      onClose();
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error?.message || "Something went wrong",
        icon: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const isEditMode = Boolean(currentOurProjectCategory);

  return (
    <UiModal
      isOpen={isOpen}
      onClose={onClose}
      eyebrow="Our Projects"
      title={isEditMode ? "Edit Category" : "Add Category"}
      as="form"
      onSubmit={handleSubmit}
      footer={
        <>
          <button
            type="button"
            onClick={onClose}
            className="ui-btn-cancel min-w-[108px]"
            disabled={loading}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="ui-btn-save min-w-[108px]"
            disabled={loading}
          >
            {loading ? "Saving..." : isEditMode ? "Update" : "Save"}
          </button>
        </>
      }
    >
      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Category details</h4>
        <div className="space-y-4">
          <div>
            <label className="ui-form-label">
              Sub Category Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              maxLength={MAX_NAME}
              placeholder="Enter sub category name"
              className={`ui-input-field ${
                errors.name ? "border-brand-error" : ""
              }`}
            />
            <div className="mt-1 flex justify-between">
              {errors.name ? (
                <p className="ui-form-error">{errors.name}</p>
              ) : (
                <span />
              )}
              <p className="text-xs text-gray-500">
                {formData.name.length}/{MAX_NAME} characters
              </p>
            </div>
            <UrduInput
              label="ذیلی زمرہ کا نام"
              name="subCategoryName_ur"
              value={formData.subCategoryName_ur}
              onChange={handleInputChange}
            />
          </div>

          <div>
            <label className="ui-form-label">
              Parent Category <span className="text-red-500">*</span>
            </label>
            <select
              name="parentCategoryId"
              value={formData.parentCategoryId}
              onChange={handleInputChange}
              className={`ui-select-field ${
                errors.parentCategoryId ? "border-brand-error" : ""
              }`}
            >
              <option value="">Select parent category</option>
              {[...parentCategories]
                .sort((a, b) => Number(a.id) - Number(b.id))
                .map((category) => (
                  <option key={category.id} value={String(category.id)}>
                    {getParentCategoryLabel(category, parentCategories)}
                  </option>
                ))}
            </select>
            {errors.parentCategoryId ? (
              <p className="ui-form-error">{errors.parentCategoryId}</p>
            ) : null}
          </div>

          <div>
            <label className="ui-form-label">Custom URL</label>
            <input
              type="text"
              name="customUrl"
              value={formData.customUrl}
              onChange={handleInputChange}
              placeholder="e.g. kitchen-finishing"
              className={`ui-input-field ${
                errors.customUrl ? "border-brand-error" : ""
              }`}
            />
            {errors.customUrl ? (
              <p className="ui-form-error">{errors.customUrl}</p>
            ) : (
              <p className="mt-1 text-xs text-gray-500">
                Optional. Leave empty to generate from the name.
              </p>
            )}
          </div>

          <div>
            <label className="ui-form-label">Description</label>
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={3}
              maxLength={MAX_DESCRIPTION}
              placeholder="Enter sub category description (optional)"
              className="ui-textarea-field"
            />
            <p className="mt-1 text-right text-xs text-gray-500">
              {formData.description.length}/{MAX_DESCRIPTION} characters
            </p>
            <UrduInput
              label="تفصیل"
              name="description_ur"
              value={formData.description_ur}
              onChange={handleInputChange}
              type="textarea"
              rows={2}
            />
          </div>
        </div>
      </section>

      <section className="ui-modal-section">
        <h4 className="ui-modal-section-title">Media</h4>
        <div className="space-y-3">
          <label className="ui-form-label">
            Category Image <span className="text-red-500">*</span>
          </label>

          <input
            id="our-project-category-image"
            type="file"
            accept="image/*"
            onChange={handleImageChange}
            className="hidden"
          />

          {imagePreview ? (
            <div className="relative">
              <div className="h-48 w-full overflow-hidden rounded-lg border-2 border-gray-200 bg-gray-100">
                <ImageWithFallback
                  src={imagePreview}
                  alt="Category preview"
                  className="h-full w-full object-contain"
                />
              </div>
              <button
                type="button"
                onClick={removeImage}
                className="absolute right-2 top-2 rounded-full bg-red-500 p-2 text-white transition-colors hover:bg-red-600"
                title="Remove image"
              >
                <TrashIcon className="h-4 w-4" />
              </button>
              <label
                htmlFor="our-project-category-image"
                className="mt-2 inline-flex cursor-pointer text-sm font-medium text-[#982598] hover:underline"
              >
                Change image
              </label>
            </div>
          ) : (
            <label
              htmlFor="our-project-category-image"
              className="flex h-48 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-2 border-dashed border-gray-300 bg-gray-50 transition-colors hover:border-[#982598]/50 hover:bg-[#982598]/5"
            >
              <span className="mb-3 flex h-12 w-12 items-center justify-center rounded-full bg-white shadow-sm">
                <CameraIcon className="h-6 w-6 text-[#982598]" />
              </span>
              <span className="font-medium text-gray-700">Upload Image</span>
              <span className="mt-1 text-sm text-gray-400">
                Required size: {REQUIRED_WIDTH} × {REQUIRED_HEIGHT} pixels
              </span>
            </label>
          )}

          {errors.image ? <p className="ui-form-error">{errors.image}</p> : null}
        </div>
      </section>
    </UiModal>
  );
}

export default OurProjectCategoryModal;
